import React, { useCallback, useEffect, useState } from 'react';
import myTraderaItemActions from '../../state/actions/my-tradera-items';
import purchaseOrderActions from '../../state/actions/purchase-order';
import { getInitialFilters } from '../../helpers/payloads/initial-payload';
import { setFiltersForSelectedPage } from '../../state/actions/filters';
import { LIST_VIEW_TYPES, PAGE_LIST_TYPES } from '../../constants/list-types';
import featureToggles, {
    errors as featureToggleErrors
} from '../../utils/feature-toggles';
import { useTranslator } from 'tradera-lang/translate';
import { useDayJs } from 'tradera-hooks/use-day-js';
import {
    openChangeShipping,
    error,
    selectMyTraderaState
} from 'tradera-apps/my-tradera/app/state/reducers/my-tradera';
import { selectMyTraderaItemsState } from 'tradera-apps/my-tradera/app/state/reducers/my-tradera-items';
import {
    selectCookiesState,
    setCookie
} from 'tradera-apps/my-tradera/app/state/reducers/cookies';
import { selectFiltersState } from 'tradera-apps/my-tradera/app/state/selectors/filters';
import { selectPurchaseOrderState } from 'tradera-apps/my-tradera/app/state/selectors/purchase-order';
import { getItemFromPurchaseOrderId } from 'tradera-apps/my-tradera/app/state/selectors/items';
import { useDispatch, useSelector } from 'react-redux';
import { waitForBackend } from '../../helpers/payloads/wait-for-backend';
import {
    removeHashFromUrl,
    shouldMarkAsPaid,
    shouldOpenChangeCombinedShippingModal,
    shouldShowRequestedCombinedShippingToast,
    URL_HASHES
} from 'tradera-apps/tradera/components/vip/helpers/navigation-helper';
import { showSuccessToast } from 'tradera-services/toasts';

const modifyHistory = (match, history) => {
    return !(history.action === 'POP' && match.params.length > 0); // When using back button from a list to another (ie from "sold" to "not sold"), we should not modify the history object
};

const defaultPayload = () => {
    return {
        filters: {
            modifiers: {
                query: {},
                paging: {}
            },
            filterBar: null,
            shownFilters: null
        }
    };
};

// eslint-disable-next-line react/display-name
const withPageInit = (Component, initialPayload = defaultPayload) => props => {
    const { t } = useTranslator();
    const dispatch = useDispatch();
    const dayjs = useDayJs();

    const state = {
        filters: useSelector(selectFiltersState),
        myTradera: useSelector(selectMyTraderaState),

        myTraderaItems: useSelector(selectMyTraderaItemsState),
        breakpoint: useSelector(selectMyTraderaState).breakpoint || {},
        page:
            useSelector(selectMyTraderaState).page[props.location.pathname] ||
            false,
        purchaseOrder: useSelector(selectPurchaseOrderState),
        sellerEmail: useSelector(state => state.member.memberEmail)
    };

    const stateCookies = useSelector(selectCookiesState);
    const { isCombinedShippingEnabled } = state.myTradera;
    const [pageListType, setPageListType] = useState();
    const { match } = props;
    const { LIST_VIEW_TYPE } = stateCookies;

    const listViewTypeWithFallBack =
        stateCookies['LIST_VIEW_TYPE_' + state.page.listType] || LIST_VIEW_TYPE;
    const listViewType = stateCookies['LIST_VIEW_TYPE_' + state.page.listType];

    useEffect(() => {
        let initialState = initialPayload ? initialPayload(t) : null;

        if (!featureToggles.isMyTraderaElasticEnabled) {
            dispatch(error(featureToggleErrors.ELASTIC_MYTRADERA));
        }

        if (initialState) {
            dispatch(
                setFiltersForSelectedPage(
                    getInitialFilters(initialState.filters)
                )
            );
        }
    }, [dispatch, t]);

    useEffect(() => {
        const getPurchaseOrderIdFromHash = hash =>
            document.location.hash.split(hash)[1];

        const removeHashAndSetNormalList = () => {
            removeHashFromUrl();
            dispatch(
                setCookie({
                    name: `LIST_VIEW_TYPE_${state.page.listType}`,
                    value: LIST_VIEW_TYPES.NORMAL
                })
            );
        };

        const itemsHasLoaded =
            state.myTraderaItems?.itemsOrderedByID?.length > 0;

        const changeShippingPurchaseOrderId = getPurchaseOrderIdFromHash(
            URL_HASHES.openChangeCombinedShippingModal
        );
        if (
            shouldOpenChangeCombinedShippingModal() &&
            changeShippingPurchaseOrderId &&
            itemsHasLoaded
        ) {
            const item = getItemFromPurchaseOrderId(
                state.myTraderaItems,
                changeShippingPurchaseOrderId
            );
            dispatch(
                openChangeShipping({
                    purchaseOrderId: changeShippingPurchaseOrderId,
                    receiverCountry:
                        item?.transaction.buyer.address.countryCodeIso2
                })
            );
            removeHashAndSetNormalList();
        }

        const markAsPaidPurchaseOrderId = getPurchaseOrderIdFromHash(
            URL_HASHES.markAsPaid
        );
        if (shouldMarkAsPaid() && markAsPaidPurchaseOrderId) {
            dispatch(
                purchaseOrderActions.updatePurchaseOrderPaidBySeller(
                    markAsPaidPurchaseOrderId,
                    state.page.listType,
                    false,
                    true
                )
            );
            removeHashAndSetNormalList();
        }

        if (shouldShowRequestedCombinedShippingToast()) {
            showSuccessToast(
                'checkout_shipping_requestCombinedShippingPriceInfo',
                'checkout_shipping_combinedShippingPriceRequested'
            );
            removeHashAndSetNormalList();
        }
    }, [dispatch, state.myTraderaItems, state.page.listType]);

    const setDefaultListViewType = useCallback(() => {
        if (window.location.search.length !== 0) {
            return null;
        }
        if (
            state.page.listType === PAGE_LIST_TYPES.BUYER_PURCHASES &&
            !listViewType
        ) {
            return dispatch(
                setCookie({
                    name: `LIST_VIEW_TYPE_${state.page.listType}`,
                    value: LIST_VIEW_TYPES.NORMAL
                })
            );
        }
        if (
            isCombinedShippingEnabled &&
            (state.page.listType === PAGE_LIST_TYPES.SELLER_SOLD ||
                state.page.listType === PAGE_LIST_TYPES.BUYER_PURCHASES) &&
            !listViewType
        ) {
            return dispatch(
                setCookie({
                    name: `LIST_VIEW_TYPE_${state.page.listType}`,
                    value: LIST_VIEW_TYPES.NORMAL
                })
            );
        }
        if (
            state.page.listType !== PAGE_LIST_TYPES.SELLER_SOLD &&
            state.page.listType !== PAGE_LIST_TYPES.BUYER_PURCHASES &&
            !listViewType
        ) {
            return dispatch(
                setCookie({
                    name: 'LIST_VIEW_TYPE',
                    value: LIST_VIEW_TYPES.BASIC
                })
            );
        }
    }, [
        dispatch,
        state.page.listType,
        isCombinedShippingEnabled,
        listViewType
    ]);

    useEffect(() => {
        setDefaultListViewType();
    }, [setDefaultListViewType]);

    useEffect(() => {
        setPageListType(state.page.listType);
    }, [state.page.listType]);

    const getData = useCallback(() => {
        const shouldWaitForBackend = waitForBackend();
        if (!pageListType) {
            return null;
        }
        if (listViewTypeWithFallBack === LIST_VIEW_TYPES.NORMAL) {
            return dispatch(
                purchaseOrderActions.runPurchaseOrders({
                    page: state.page,
                    shouldScrollToInitialScroll: false,
                    listViewType: listViewTypeWithFallBack,
                    waitForBackend: shouldWaitForBackend,
                    dontModifyHistory: !modifyHistory(match, history),
                    dayjs
                })
            );
        }
        return dispatch(
            myTraderaItemActions.runSearch(
                state.page,
                false,
                listViewTypeWithFallBack,
                shouldWaitForBackend,
                !modifyHistory(match, history)
            )
        );
    }, [
        dispatch,
        state.page,
        pageListType,
        listViewTypeWithFallBack,
        match,
        dayjs
    ]);

    useEffect(() => {
        getData();
    }, [pageListType, getData]);

    const cookies = {
        ...stateCookies,
        LIST_VIEW_TYPE: listViewTypeWithFallBack
    };

    return (
        <Component
            dispatch={dispatch}
            cookies={cookies}
            {...state}
            {...props}
        />
    );
};

export default withPageInit;
